import {
  useCallback,
  useEffect,
  useRef,
  useState,
  PropsWithChildren
} from 'react'
import clsx from 'clsx'
import useTooltip from '@/hooks/useTooltip'
import { off, on } from '@/utils/evenListening'
import { ROOT_EMEMENT_ID } from './settings'
import styles from './index.module.sass'

type TooltipCloseButton = {
  onClose: () => void;
}

type TooltipButton = {
  text: string;
  onClick: () => void;
}

interface TooltipInterface {
  id: string;
  target: string;
  atBottom?: boolean;
  fixed?: boolean;
  onDisplayed?: () => void;
  onDismissed?: () => void;
  text?: string;
  closeButton?: TooltipCloseButton;
  primaryButton?: TooltipButton;
  secondaryButton?: TooltipButton;
}

const PADDING_PANEL = 0
const ARROW_SIZE = 7
const GAP_SIZE = 4

const Tooltip = ({
  id,
  atBottom,
  fixed,
  target,
  onDisplayed,
  onDismissed,
  text,
  closeButton,
  primaryButton,
  secondaryButton
}: PropsWithChildren<TooltipInterface>) => {
  const { remove } = useTooltip()
  const [show, setShow] = useState(false)
  const [visible, setVisible] = useState(false)
  const [containerStyle, setContainerStyle] = useState(undefined)

  const containerRef = useRef<HTMLDivElement>()

  const dismiss = useCallback(() => {
    if (!show) {
      return
    }
    onDismissed?.()
    setShow(false)
    remove(id)
  }, [onDismissed, show])

  const updatePosition = useCallback(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const _target = document.querySelector(target) as HTMLElement
    if (!_target || !_target.offsetWidth) {
      dismiss()
      return
    }

    const targetRect = _target.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const appRect = document.querySelector(ROOT_EMEMENT_ID).getBoundingClientRect()

    let containerLeft =
      targetRect.left - (containerRect.width - targetRect.width) / 2
    if (containerLeft > appRect.right - PADDING_PANEL - containerRect.width) {
      containerLeft = appRect.right - PADDING_PANEL - containerRect.width
    }
    if (containerLeft < appRect.left + PADDING_PANEL) {
      containerLeft = appRect.left + PADDING_PANEL
    }

    const arrowLeft =
      targetRect.left - containerLeft + targetRect.width / 2 - ARROW_SIZE

    let offset = 0
    if (
      arrowLeft >
      containerRect.width - containerRect.height / 2 - ARROW_SIZE * 2
    ) {
      offset =
        arrowLeft -
        (containerRect.width - containerRect.height / 2 - ARROW_SIZE * 2)
    }
    if (arrowLeft < containerRect.height / 2) {
      offset = arrowLeft - containerRect.height / 2
    }

    const yOffset = fixed ? (window.scrollY >= 0 ? 0 : window.scrollY) : window.pageYOffset
    const xOffset = fixed ? 0 : window.pageXOffset

    const style = {
      position: fixed ? 'fixed' : undefined,
      top: atBottom
        ? (yOffset + targetRect.bottom + ARROW_SIZE + GAP_SIZE) + 'px'
        : (yOffset + targetRect.top - containerRect.height - ARROW_SIZE - GAP_SIZE) + 'px',
      left: (xOffset + containerLeft + offset) + 'px',
      visibility: visible ? 'visible' : 'hidden',
      '--arrow-position': arrowLeft - offset + 'px'
    }

    setContainerStyle(style)
  }, [dismiss, target, atBottom, fixed, visible])

  useEffect(() => {
    if (!show) return

    const _target = document.querySelector(target) as HTMLElement
    if (!_target || !_target.offsetWidth) {
      dismiss()
      return
    }

    updatePosition()
    on(window, 'resize', updatePosition, { passive: true })
    on(window, 'scroll', updatePosition, { passive: true })

    const mutationObserver = new MutationObserver(updatePosition)
    mutationObserver.observe(document.querySelector(ROOT_EMEMENT_ID), {
      childList: true,
      subtree: true
    })

    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const { target: hook, isIntersecting } = entry
        if (_target.isSameNode(hook)) {
          setVisible(isIntersecting)
        }
      }
    }, {
      rootMargin: '0px'
    })
    intersectionObserver.observe(_target)

    return () => {
      off(window, 'resize', updatePosition, { passive: true })
      off(window, 'scroll', updatePosition, { passive: true })
      mutationObserver.disconnect()
      intersectionObserver.disconnect()
    }
  }, [show, updatePosition, target])

  useEffect(() => {
    setShow(true)
    onDisplayed?.()
    return () => dismiss()
  }, [])

  return (
    <div
      ref={containerRef}
      className={styles.tooltip}
      style={containerStyle}
    >
      <div className={clsx(styles.arrwoBox, atBottom ? styles.up : styles.bottom)} />
      <div className={styles.content}>
        <span className={styles.textBox}>
          <div className={styles.text}>{text}</div>
        </span>
        {closeButton
          ? <span
              className={styles.close}
              onClick={() => {
                closeButton?.onClose?.()
                dismiss()
              }}
            />
          : null}
      </div>
      {primaryButton || secondaryButton
        ? (
          <div className={styles.options}>
            {secondaryButton
              ? <span
                  className={styles.learnMore}
                  onClick={() => {
                    secondaryButton.onClick()
                    dismiss()
                  }}
                >
                  {secondaryButton.text}
                </span>
              : <></>}
            {primaryButton
              ? <span
                  className={styles.button}
                  onClick={() => {
                    primaryButton.onClick()
                    dismiss()
                  }}
                >
                  {primaryButton.text}
                </span>
              : <></>}
          </div>
          )
        : <></>}
    </div>
  )
}

Tooltip.defaultProps = {
  onDismissed: undefined,
  text: ''
}

export default Tooltip
